import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import TuneIcon from '@mui/icons-material/Tune';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import EditIcon from '@mui/icons-material/Edit';

// ── localStorage helper for custom dataset names ──────────────
const CUSTOM_NAMES_KEY = 'ds_custom_names';
function getCustomNames(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(CUSTOM_NAMES_KEY) || '{}'); } catch { return {}; }
}
function setCustomName(tableName: string, name: string) {
  const names = getCustomNames();
  names[tableName] = name;
  localStorage.setItem(CUSTOM_NAMES_KEY, JSON.stringify(names));
}
function getDisplayName(dataset: { name: string; table_name: string }): string {
  const custom = getCustomNames()[dataset.table_name];
  return custom || dataset.name;
}

interface Dataset {
  id: number;
  name: string;
  table_name: string;
  project_id: number;
  uploaded_at: string;
  filtered_datasets?: Dataset[];
}

interface Project {
  id: number;
  name: string;
  user_id: number;
  created_at: string;
  dataset_count: number;
}

interface DatasetManagerProps {
  project: Project;
  onDatasetSelect: (dataset: Dataset | null) => void;
  selectedDataset: Dataset | null;
  onNavigateToDataManipulation: (dataset: Dataset) => void;
  onNavigateToAnalysis: (dataset: Dataset) => void;
  onNavigateToChatWithData: (dataset: Dataset) => void;
}

// ── Single dataset row ──────────────────────────────────────
const DatasetRow: React.FC<{
  dataset: Dataset;
  isSelected: boolean;
  isFiltered?: boolean;
  onSelect: (d: Dataset) => void;
  onDelete: (d: Dataset) => void;
  onAnalysis: (d: Dataset) => void;
  onChat: (d: Dataset) => void;
  onManipulate: (d: Dataset) => void;
  onRename?: (d: Dataset) => void;
}> = ({ dataset, isSelected, isFiltered, onSelect, onDelete, onAnalysis, onChat, onManipulate, onRename }) => (
  <Box
    onClick={() => onSelect(dataset)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: isFiltered ? 4 : 3,
      py: 2,
      borderBottom: '1px solid #F3F4F6',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#F9FAFB' : isFiltered ? '#FAFAFA' : '#FFFFFF',
      transition: 'background-color 0.12s ease',
      '&:hover': { backgroundColor: '#F9FAFB' },
      '&:hover .row-actions': { opacity: 1 },
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 32,
        height: 32,
        backgroundColor: isFiltered ? '#F3F4F6' : isSelected ? '#111111' : '#F3F4F6',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {isFiltered
        ? <FilterAltIcon sx={{ fontSize: 14, color: '#6B7280' }} />
        : <TableChartIcon sx={{ fontSize: 14, color: isSelected ? '#FFFFFF' : '#6B7280' }} />
      }
    </Box>

    {/* Info */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography
          sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111111' }}
          noWrap
        >
          {getDisplayName(dataset)}
        </Typography>
        {isFiltered && (
          <Chip label="Filtered" size="small" sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }} />
        )}
        {onRename && (
          <Tooltip title="Rename">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onRename(dataset); }}
              sx={{ opacity: 0.4, '&:hover': { opacity: 1 }, p: 0.25 }}>
              <EditIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
        Uploaded {new Date(dataset.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </Typography>
    </Box>

    {/* Action buttons - visible on hover */}
    <Box
      className="row-actions"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        opacity: { xs: 1, md: isSelected ? 1 : 0 },
        transition: 'opacity 0.12s ease',
        flexShrink: 0,
      }}
      onClick={e => e.stopPropagation()}
    >
      <Tooltip title="Ask AI">
        <Button
          size="small"
          variant="outlined"
          startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
          onClick={() => onChat(dataset)}
          sx={{
            fontSize: '0.75rem',
            py: 0.375,
            px: 1.25,
            borderColor: '#E5E7EB',
            color: '#374151',
            minWidth: 'auto',
          }}
        >
          Ask AI
        </Button>
      </Tooltip>
      <Tooltip title="Analyze">
        <Button
          size="small"
          variant="outlined"
          startIcon={<BarChartIcon sx={{ fontSize: 14 }} />}
          onClick={() => onAnalysis(dataset)}
          sx={{
            fontSize: '0.75rem',
            py: 0.375,
            px: 1.25,
            borderColor: '#E5E7EB',
            color: '#374151',
            minWidth: 'auto',
          }}
        >
          Analyze
        </Button>
      </Tooltip>
      <Tooltip title="Transform">
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: 14 }} />}
          onClick={() => onManipulate(dataset)}
          sx={{
            fontSize: '0.75rem',
            py: 0.375,
            px: 1.25,
            borderColor: '#E5E7EB',
            color: '#374151',
            minWidth: 'auto',
          }}
        >
          Transform
        </Button>
      </Tooltip>
      <Tooltip title="Delete dataset">
        <IconButton
          size="small"
          onClick={() => onDelete(dataset)}
          sx={{ color: '#9CA3AF', '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' } }}
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>

    <ArrowForwardIcon sx={{ fontSize: 14, color: '#D1D5DB', flexShrink: 0 }} />
  </Box>
);

// ── Main component ──────────────────────────────────────────
const DatasetManager: React.FC<DatasetManagerProps> = ({
  project,
  onDatasetSelect,
  selectedDataset,
  onNavigateToDataManipulation,
  onNavigateToAnalysis,
  onNavigateToChatWithData,
}) => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [datasetName, setDatasetName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [renameTarget, setRenameTarget] = useState<Dataset | null>(null);
  const [renameName, setRenameName] = useState('');

  const handleRename = (d: Dataset) => {
    setRenameTarget(d);
    setRenameName(getDisplayName(d));
  };

  const confirmRename = () => {
    if (renameTarget && renameName.trim()) {
      setCustomName(renameTarget.table_name, renameName.trim());
      setRenameTarget(null);
      setRenameName('');
      // Force re-render
      setDatasets(prev => [...prev]);
    }
  };

  const fetchDatasets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/projects/${project.id}/datasets`, { credentials: 'include' });
      if (res.ok) {
        setDatasets(await res.json());
      } else {
        const data = await res.json();
        setError(Array.isArray(data.detail) ? data.detail.map((e: any) => e.msg).join(', ') : data.detail || 'Failed to load datasets');
      }
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchDatasets(); }, [fetchDatasets]);

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setError('Please select a CSV file (.csv)');
      return;
    }
    setError('');
    setSelectedFile(file);
    if (!datasetName) setDatasetName(file.name.replace(/\.csv$/i, ''));
  };

  const handleUpload = async () => {
    if (!selectedFile || !datasetName.trim()) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const res = await fetch(
        `/api/upload-csv/?project_id=${project.id}&dataset_name=${encodeURIComponent(datasetName.trim())}`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      if (res.ok) {
        setSelectedFile(null);
        setDatasetName('');
        setOpenUpload(false);
        fetchDatasets();
      } else {
        const data = await res.json();
        setError(Array.isArray(data.detail) ? data.detail.map((e: any) => e.msg).join(', ') : data.detail || 'Upload failed');
      }
    } catch {
      setError('Network error.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (dataset: Dataset) => {
    if (!window.confirm(`Delete "${dataset.name}"? This cannot be undone.`)) return;
    const isFiltered = dataset.table_name.includes('_') && dataset.table_name.split('_').length > 3;
    try {
      const url = isFiltered
        ? `/api/projects/${project.id}/filtered-datasets/${encodeURIComponent(dataset.table_name)}`
        : `/api/projects/${project.id}/datasets/${dataset.id}`;
      const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setDatasets(prev =>
          prev.map(d => {
            if (d.id === dataset.id) return null as any;
            if (d.filtered_datasets) {
              return { ...d, filtered_datasets: d.filtered_datasets.filter(f => f.table_name !== dataset.table_name) };
            }
            return d;
          }).filter(Boolean)
        );
        if (selectedDataset?.table_name === dataset.table_name) onDatasetSelect(null);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to delete dataset');
      }
    } catch {
      setError('Network error.');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1.5, mb: 3 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 500 }}>
          {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
          onClick={() => setOpenUpload(true)}
          sx={{ backgroundColor: '#111111', color: '#FFFFFF', '&:hover': { backgroundColor: '#2D2D2D' }, width: { xs: '100%', sm: 'auto' } }}
        >
          Upload CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Dataset list */}
      <Box sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          [1, 2, 3].map(i => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderBottom: '1px solid #F3F4F6' }}>
              <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '6px', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={200} height={16} sx={{ mb: 0.5 }} />
                <Skeleton width={120} height={12} />
              </Box>
            </Box>
          ))
        ) : datasets.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <TableChartIcon sx={{ fontSize: 40, color: '#E5E7EB', mb: 2 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#111111', mb: 0.75 }}>
              No datasets yet
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#9CA3AF', mb: 3, maxWidth: 360, mx: 'auto' }}>
              Upload a CSV file to get started. You can analyze up to 1 million rows.
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setOpenUpload(true)}
              sx={{ backgroundColor: '#111111', color: '#FFFFFF' }}
            >
              Upload Your First Dataset
            </Button>
          </Box>
        ) : (
          datasets.map((dataset, index) => (
            <React.Fragment key={dataset.id}>
              <DatasetRow
                dataset={dataset}
                isSelected={selectedDataset?.id === dataset.id}
                onSelect={onDatasetSelect}
                onDelete={handleDelete}
                onAnalysis={onNavigateToAnalysis}
                onChat={onNavigateToChatWithData}
                onManipulate={onNavigateToDataManipulation}
                onRename={handleRename}
              />
              {dataset.filtered_datasets?.map(fd => (
                <DatasetRow
                  key={fd.id || fd.table_name}
                  dataset={fd}
                  isSelected={selectedDataset?.table_name === fd.table_name}
                  isFiltered
                  onSelect={onDatasetSelect}
                  onDelete={handleDelete}
                  onAnalysis={onNavigateToAnalysis}
                  onChat={onNavigateToChatWithData}
                  onManipulate={onNavigateToDataManipulation}
                  onRename={handleRename}
                />
              ))}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={openUpload}
        onClose={() => { if (!uploading) { setOpenUpload(false); setSelectedFile(null); setDatasetName(''); setError(''); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Dataset</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', mb: 3 }}>
            Upload a CSV file to analyze. Supports files up to 1 million rows.
          </Typography>

          {/* Drag & drop zone */}
          <Box
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFileChange(e.dataTransfer.files[0] ?? null);
            }}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? '#111111' : '#E5E7EB'}`,
              borderRadius: '8px',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragOver ? '#F9FAFB' : '#FAFAFA',
              transition: 'all 0.15s ease',
              mb: 3,
              '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 32, color: '#9CA3AF', mb: 1 }} />
            {selectedFile ? (
              <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#111111' }}>
                  {selectedFile.name}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · Click to change
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                  Drag your CSV file here
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  or click to browse · .csv files only
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={e => handleFileChange(e.target.files?.[0] ?? null)}
            />
          </Box>

          <TextField
            label="Dataset name"
            value={datasetName}
            onChange={e => setDatasetName(e.target.value)}
            fullWidth
            placeholder="e.g. Q1 Sales Data"
          />

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenUpload(false); setSelectedFile(null); setDatasetName(''); setError(''); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || !selectedFile || !datasetName.trim()}
            sx={{ backgroundColor: '#111111', color: '#FFFFFF' }}
          >
            {uploading ? 'Uploading…' : 'Upload Dataset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameTarget} onClose={() => setRenameTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Rename dataset</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', mb: 2 }}>
            Give this dataset a more descriptive name:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmRename(); }}
            placeholder="e.g. High Value Customers Q1"
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={confirmRename} disabled={!renameName.trim()}
            sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', '&:hover': { bgcolor: '#2D2D2D' } }}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatasetManager;
